/**
 * AdminRoute - Protected route wrapper for admin-only pages
 * Redirects non-admin users to an unauthorized page
 */

import React, { type JSX } from 'react';
import { useAppSelector } from '../../hooks/reduxHooks';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';

interface AdminRouteProps {
  children: React.ReactNode;
}

export default function AdminRoute({ children }: AdminRouteProps): JSX.Element {
  const isAdmin = useAppSelector((state) => state.userAuthAndAdminSlice.isAdmin);
  const isAuthenticated = useAppSelector((state) => state.userAuthAndAdminSlice.isAuthenticated);
  const userEmail = useAppSelector((state) => state.userAuthAndAdminSlice.userEmail);

  // If not authenticated, show login required message
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center p-8">
          <ShieldExclamationIcon className="mx-auto h-16 w-16 text-gray-400" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Authentication Required</h1>
          <p className="mt-2 text-gray-600">
            Please sign in to access this page.
          </p>
        </div>
      </div>
    );
  }

  // If authenticated but not admin, show 403 forbidden
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center p-8">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
            <ShieldExclamationIcon className="h-10 w-10 text-red-600" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Access Denied</h1>
          <p className="mt-2 text-gray-600">
            You do not have permission to access the admin area.
          </p>
          <p className="mt-4 text-sm text-gray-500">
            Signed in as: {userEmail}
          </p>
          <p className="mt-2 text-sm text-gray-500">
            If you believe this is an error, please contact your administrator
            to request the <code className="bg-gray-100 px-1 py-0.5 rounded">DealOS.Admin</code> role.
          </p>
          <div className="mt-6">
            <a
              href="/"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Return to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated and is admin - render children
  return <>{children}</>;
}
